import { usePromptStore } from '@/stores/PromptStore.ts';
import {
    type VariableConfig,
    type VariableType,
    VariableTypes,
} from '@/types/edit/VariableConfig.ts';
import { DefaultVariableConfigs } from '@/constants/DefaultVariableConfigs.ts';
import type { PromptConfig } from '@/types/edit/PromptConfig.ts';
import type { ProfileConfig } from '@/types/edit/ProfileConfig.ts';
import {
    PROMPT_OUTPUT_VARIABLE_PREFIX,
    PROMPT_USER_INPUT_VARIABLE_PREFIX,
} from '@/constants/VariablePrefixes.ts';
import { slugify } from '@/utils/RandomUtils.ts';
import { v4 as uuidv4 } from 'uuid';
import { MAX_CUSTOM_VARIABLES } from '@/constants/EditMaxes.ts';
import { EditLimitReachedError } from '@/errors/edit/EditLimitReachedError.ts';
import { ConfigNotFoundError } from '@/errors/edit/ConfigNotFoundError.ts';
import { InvalidInputError } from '@/errors/edit/InvalidInputError.ts';

export abstract class VariableConfigService {
    public static createCustomVariable(
        variableConfigs: Record<VariableConfig['id'], VariableConfig>,
    ): {
        variableConfigs: Record<VariableConfig['id'], VariableConfig>;
        newVariableConfigId: VariableConfig['id'];
    } {
        const newVariableConfigId = uuidv4();

        const customVariables = Object.values(variableConfigs).filter((v) => v.type === 'CUSTOM');

        if (customVariables.length >= MAX_CUSTOM_VARIABLES) {
            throw new EditLimitReachedError('Max variables reached');
        }

        variableConfigs = {
            ...variableConfigs,
            [newVariableConfigId]: {
                id: newVariableConfigId,
                key: `CUSTOM_VARIABLE_${customVariables.length}`,
                type: 'CUSTOM',
                valueType: 'string',
                value: '',
                description: undefined,
                editable: true,
            },
        };

        return {
            variableConfigs,
            newVariableConfigId,
        };
    }

    public static getVariableConfigs = (
        promptConfigs: Record<PromptConfig['id'], PromptConfig>,
        profileConfigId: ProfileConfig['id'] | undefined,
        types: VariableType[],
    ): Record<VariableConfig['id'], VariableConfig> => {
        let result: Record<VariableConfig['id'], VariableConfig> = {};

        // Default variables
        if (types.includes('DEFAULT')) {
            result = {
                ...result,
                ...DefaultVariableConfigs,
            };
        }

        // Custom variables
        if (types.includes('CUSTOM')) {
            result = {
                ...result,
                ...this.getCustomVariableConfigs(),
            };
        }

        // Prompt output variables
        if (types.includes('PROMPT_OUTPUT')) {
            const outputPrompts = Object.values(promptConfigs).filter(
                (p) => p.profileId === profileConfigId && p.formatAs !== 'choice',
            );
            for (const prompt of outputPrompts) {
                result = {
                    ...result,
                    [prompt.id]: {
                        id: prompt.id,
                        key: this.getPromptOutputVariableKey(prompt.name),
                        type: 'PROMPT_OUTPUT',
                        valueType: 'string',
                        value: undefined,
                        description: `The output of the "${prompt.name}" prompt is stored in this variable. The value in this variable is only available after the prompt has fully finished. The variable will be empty until then.`,
                        editable: false,
                    },
                };
            }
        }

        // Prompt user input variables
        if (types.includes('PROMPT_USER_INPUT')) {
            const userInputPrompts = Object.values(promptConfigs).filter(
                (p) => p.profileId === profileConfigId && p.formatAs === 'choice',
            );
            for (const prompt of userInputPrompts) {
                result = {
                    ...result,
                    [prompt.id]: {
                        id: prompt.id,
                        key: this.getPromptUserInputVariableKey(prompt.name),
                        type: 'PROMPT_USER_INPUT',
                        valueType: 'string',
                        value: undefined,
                        description: `The user input made on prompt "${prompt.name}" is stored in this variable. The value in this variable is only available after user input has been made. The variable will be empty until then.`,
                        editable: false,
                    },
                };
            }
        }

        return result;
    };

    public static getCustomVariableConfigs = (): Record<VariableConfig['id'], VariableConfig> => {
        const promptStore = usePromptStore();

        return promptStore.variableConfigs;
    };

    public static getVariableConfig = (
        promptConfigs: Record<PromptConfig['id'], PromptConfig>,
        profileConfigId: ProfileConfig['id'] | undefined,
        id: VariableConfig['id'],
    ): VariableConfig => {
        return this.getVariableConfigs(promptConfigs, profileConfigId, VariableTypes)[id];
    };

    public static getPromptUserInputVariableKey(promptConfigName: PromptConfig['name']): string {
        return `${PROMPT_USER_INPUT_VARIABLE_PREFIX}${slugify(promptConfigName, true).toUpperCase()}`;
    }

    public static getPromptOutputVariableKey(promptConfigName: PromptConfig['name']): string {
        return `${PROMPT_OUTPUT_VARIABLE_PREFIX}${slugify(promptConfigName, true).toUpperCase()}`;
    }

    public static updateCustomVariableKey(
        variableConfigs: Record<VariableConfig['id'], VariableConfig>,
        variableConfigId: VariableConfig['id'],
        key: VariableConfig['key'],
    ): {
        variableConfigs: Record<VariableConfig['id'], VariableConfig>;
    } {
        const variableConfig = variableConfigs[variableConfigId];
        if (!variableConfig) {
            throw new ConfigNotFoundError(`Variable config with id ${variableConfigId} not found`);
        }

        if (variableConfig.type !== 'CUSTOM') {
            throw new InvalidInputError(
                `Variable config with id ${variableConfigId} is not a custom variable`,
            );
        }

        variableConfigs = {
            ...variableConfigs,
            [variableConfigId]: {
                ...variableConfigs[variableConfigId],
                key: key,
            },
        };

        return {
            variableConfigs,
        };
    }

    public static updateCustomVariableValueType(
        variableConfigs: Record<VariableConfig['id'], VariableConfig>,
        variableConfigId: VariableConfig['id'],
        valueType: VariableConfig['valueType'],
    ): {
        variableConfigs: Record<VariableConfig['id'], VariableConfig>;
    } {
        const variableConfig = variableConfigs[variableConfigId];
        if (!variableConfig) {
            throw new ConfigNotFoundError(`Variable config with id ${variableConfigId} not found`);
        }

        if (variableConfig.type !== 'CUSTOM') {
            throw new InvalidInputError(
                `Variable config with id ${variableConfigId} is not a custom variable`,
            );
        }

        if (valueType === 'string') {
            variableConfigs = {
                ...variableConfigs,
                [variableConfigId]: {
                    ...variableConfigs[variableConfigId],
                    valueType: 'string',
                    value: '',
                },
            };
        }

        if (valueType === 'array') {
            variableConfigs = {
                ...variableConfigs,
                [variableConfigId]: {
                    ...variableConfigs[variableConfigId],
                    valueType: 'array',
                    value: [],
                },
            };
        }

        return {
            variableConfigs,
        };
    }

    public static updateCustomVariableValue(
        variableConfigs: Record<VariableConfig['id'], VariableConfig>,
        variableConfigId: VariableConfig['id'],
        value: VariableConfig['value'],
    ): {
        variableConfigs: Record<VariableConfig['id'], VariableConfig>;
    } {
        const variableConfig = variableConfigs[variableConfigId];
        if (!variableConfig) {
            throw new ConfigNotFoundError(
                `Custom variable config with id ${variableConfigId} not found`,
            );
        }

        if (variableConfig.type !== 'CUSTOM') {
            throw new InvalidInputError(
                `Variable config with id ${variableConfigId} is not a custom variable`,
            );
        }

        if (variableConfig.valueType === 'string') {
            if (typeof value !== 'string') {
                throw new InvalidInputError(
                    `Custom variable config with id ${variableConfigId} value is not a string`,
                );
            }

            variableConfigs = {
                ...variableConfigs,
                [variableConfigId]: {
                    ...variableConfigs[variableConfigId],
                    valueType: 'string',
                    value: value,
                },
            };
        }

        if (variableConfig.valueType === 'array') {
            if (!Array.isArray(value)) {
                throw new InvalidInputError(
                    `Custom variable config with id ${variableConfigId} value is not an array`,
                );
            }

            variableConfigs = {
                ...variableConfigs,
                [variableConfigId]: {
                    ...variableConfigs[variableConfigId],
                    valueType: 'array',
                    value: value,
                },
            };
        }

        return {
            variableConfigs,
        };
    }

    public static updateCustomVariableConfigs(
        variableConfigs: Record<VariableConfig['id'], VariableConfig>,
    ): void {
        const promptStore = usePromptStore();
        promptStore.setVariableConfigs(variableConfigs);
    }

    public static deleteCustomVariable(
        variableConfigs: Record<VariableConfig['id'], VariableConfig>,
        variableConfigId: VariableConfig['id'],
    ): {
        variableConfigs: Record<VariableConfig['id'], VariableConfig>;
    } {
        const { [variableConfigId]: _, ...updatedVariableConfigs } = variableConfigs;

        return {
            variableConfigs: updatedVariableConfigs,
        };
    }

    public static validate(
        variableConfigs: Record<VariableConfig['id'], VariableConfig>,
    ): string[] {
        const errors: string[] = [];
        const seenKeys: VariableConfig['key'][] = [];

        for (const variableConfig of Object.values(variableConfigs)) {
            // Key length
            if (variableConfig.key.length === 0) {
                errors.push(`[Variable "${variableConfig.key}"] Variable key cannot be empty`);
            } else if (variableConfig.key.length > 50) {
                errors.push(
                    `[Variable "${variableConfig.key}"] Variable key cannot be longer than 50 characters`,
                );
            }

            if (variableConfig.key.includes('$')) {
                errors.push(
                    `[Variable "${variableConfig.key}"] Variable key cannot contain the "$" character. This is a reserved character for variables.`,
                );
            }

            if (variableConfig.key.indexOf(' ') >= 0) {
                errors.push(
                    `[Variable "${variableConfig.key}"] Variable key cannot contain spaces`,
                );
            }

            if (seenKeys.includes(variableConfig.key)) {
                errors.push(
                    '[Variable "' +
                        variableConfig.key +
                        '] Duplicate variable key found, please make sure all variables have a unique key.',
                );
            }
            seenKeys.push(variableConfig.key);

            // Value type match
            if (variableConfig.valueType === 'string' && typeof variableConfig.value !== 'string') {
                errors.push(
                    `[Variable "${variableConfig.key}"] Variable type and value do not match (should be a string)`,
                );
            } else if (
                variableConfig.valueType === 'array' &&
                !Array.isArray(variableConfig.value)
            ) {
                errors.push(
                    `[Variable "${variableConfig.key}"] Variable type and value do not match (should be an array)`,
                );
            }
        }

        return errors;
    }
}
